import ENV from "@/utils/env";

/* Auth state storage key */
const AUTH_STATE_STORAGE_KEY = "df.auth.state-nonce";

/* Types */
interface AuthStatePayload {
  nonce: string;
  returnTo?: string;
}

/**
 * getAllowedReturnOrigins
 *
 * @description Retrieves the set of allowed return origins for the application,
 * which includes the current origin and any additional origins specified in the environment configuration.
 * This is used to validate returnTo URLs in the authentication flow to prevent open redirect vulnerabilities.
 *
 * @returns A set of allowed return origins.
 */
const getAllowedReturnOrigins = () => {
  // Start with the current origin as an allowed return origin
  const origins = new Set<string>([window.location.origin]);

  // Add specific env origins such as admin
  if (ENV.ADMIN_URL) {
    try {
      origins.add(new URL(ENV.ADMIN_URL).origin);
    } catch {
      // Ignore invalid env config and fall back to same-origin only.
    }
  }

  return origins;
};

// Custom scheme URLs (e.g. native app deep links) have opaque origins so they
// cannot be validated by origin alone — list them explicitly instead.
const ALLOWED_RETURN_URLS = new Set<string>(["df-app://auth/callback"]);

/**
 * normalizeReturnTo
 *
 * @description Validates and normalizes a returnTo URL to prevent open redirect vulnerabilities.
 * Only allows URLs that match the current origin or are included in the allowed origins list.
 *
 * @param returnTo The returnTo URL to validate and normalize.
 * @returns A normalized returnTo URL if valid, or null if invalid.
 */
export const normalizeReturnTo = (returnTo: string | null | undefined) => {
  if (!returnTo) return null;

  try {
    // Attempt to construct a URL object to validate the format and origin
    const url = new URL(returnTo, window.location.origin);
    const allowedOrigins = getAllowedReturnOrigins();

    // Only allow returnTo URLs that match the current origin or are explicitly allowed
    if (!allowedOrigins.has(url.origin)) {
      // Custom scheme URLs (e.g. df-app://) have opaque origins — fall back to exact URL match
      if (!ALLOWED_RETURN_URLS.has(url.toString())) {
        return null;
      }
      return url.toString();
    }

    // Return the path and query string for same-origin URLs, or the full URL for allowed cross-origin URLs
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    return url.toString();
  } catch {
    return null;
  }
};

/**
 * createAuthState
 *
 * @description Creates a state parameter for the Auth0 authorization request,
 * which includes a nonce for CSRF protection and an optional returnTo URL.
 * The nonce is stored in sessionStorage to be validated later in the callback route.
 *
 * @param returnTo The returnTo URL to include in the state parameter.
 * @returns An encoded state parameter for the Auth0 authorization request.
 */
export const createAuthState = (returnTo: string | null) => {
  // Normalize and validate the returnTo URL before including it in the state payload
  const normalizedReturnTo = normalizeReturnTo(returnTo);

  // Generate a unique nonce and store it in sessionStorage for later validation
  const payload: AuthStatePayload = {
    nonce: crypto.randomUUID(),
  };

  // Only include the returnTo URL in the state if it's valid to prevent open redirect vulnerabilities
  if (normalizedReturnTo) {
    payload.returnTo = normalizedReturnTo;
  }

  // Store the nonce in sessionStorage to validate it later in the callback route
  sessionStorage.setItem(AUTH_STATE_STORAGE_KEY, payload.nonce);

  // Encode the state payload as a URI component to safely include it in the Auth0 authorization request
  return encodeURIComponent(JSON.stringify(payload));
};

/**
 * parseAuthState
 *
 * @description Parses and validates the state parameter returned from Auth0 after authentication.
 * Validates the nonce against the value stored in sessionStorage to protect against CSRF attacks,
 * and normalizes the returnTo URL if present.
 *
 * @param state The state parameter returned from Auth0.
 * @returns An object containing the validation result and the normalized returnTo URL.
 */
export const parseAuthState = (state: string | null) => {
  if (!state) {
    return { isValid: false, returnTo: null };
  }

  try {
    // Retrieve the stored nonce from sessionStorage and parse the state payload
    const storedNonce = sessionStorage.getItem(AUTH_STATE_STORAGE_KEY);

    // If there's no stored nonce, the callback has already been processed (likely in StrictMode double-invoke)
    if (!storedNonce) {
      return { isValid: false, returnTo: null };
    }

    const payload = JSON.parse(decodeURIComponent(state)) as AuthStatePayload;

    // Validate the nonce to protect against CSRF attacks
    if (!payload.nonce || storedNonce !== payload.nonce) {
      // Clean up sessionStorage if validation fails
      sessionStorage.removeItem(AUTH_STATE_STORAGE_KEY);
      return { isValid: false, returnTo: null };
    }

    // Clear the nonce from sessionStorage after successful validation to prevent reuse
    sessionStorage.removeItem(AUTH_STATE_STORAGE_KEY);

    // If the nonce is valid, return the normalized returnTo URL (if present) and indicate that the state is valid
    return {
      isValid: true,
      returnTo: normalizeReturnTo(payload.returnTo),
    };
  } catch {
    // Clean up sessionStorage in case of any parsing errors to prevent stale nonces from causing issues in future auth attempts
    sessionStorage.removeItem(AUTH_STATE_STORAGE_KEY);
    return { isValid: false, returnTo: null };
  }
};
